import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Feedback } from '@noths/polaris-client-ribbons-design-system';

import { PhotoPickerDrawer } from './components/PhotoPickerDrawer';
import { PhotoPickerDropzone } from './components/PhotoPickerDropzone';
import { PhotoPickerGrid } from './components/PhotoPickerGrid';
import { PhotoPickerTrigger } from './components/PhotoPickerTrigger';
import { DEFAULT_PHOTO_PICKER_COPY } from './copy';
import { format } from './format';
import { useBlobPreviews } from './hooks/useBlobPreviews';
import { usePhotoPickerUploads } from './hooks/usePhotoPickerUploads';
import type { PhotoPickerProps } from './types';
import * as S from './PhotoPicker.styles';

export function PhotoPicker({
  acceptedFileTypes,
  copy,
  describedById,
  erroredUrls,
  max = 10,
  maxFileSizeBytes,
  min = 0,
  onChange,
  onClose: onCloseProp,
  onConfirm: onConfirmProp,
  onFilesRejected,
  onPickAttempt,
  onUploadAttempt,
  onUploadError,
  open: openProp,
  previewFor: previewForProp,
  rejectionLines,
  requestUploadSlots,
  required,
  requirementsLines,
  triggerLabel,
  uploadFiles,
  value,
}: PhotoPickerProps) {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? openProp : internalOpen;

  const mergedCopy = useMemo(() => ({ ...DEFAULT_PHOTO_PICKER_COPY, ...copy }), [copy]);

  const blob = useBlobPreviews();
  const uploads = usePhotoPickerUploads({ isOpen, max, requestUploadSlots, uploadFiles });

  // Draft URLs shown inside the drawer while it's open. `null` when closed.
  // Selected files sit in `pendingFiles` as blob URLs until confirm; the upload
  // only runs on confirm. Deletes/reorders mutate the draft locally;
  // `onChange` only fires on confirm. Works the same for fresh and edit flows.
  const [draftUrls, setDraftUrls] = useState<string[] | null>(null);
  const pendingFilesRef = useRef<Map<string, File>>(new Map());
  const drawerUrls = draftUrls ?? value;
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  // Blob URLs in the draft whose upload failed in the last Confirm attempt;
  // drives the per-tile red-border + faded-image state. Cleared on retry,
  // drawer reopen, and individual delete so it never lingers stale.
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(() => new Set());

  // On open: seed the draft from `value`. On close: revoke pending blob URLs
  // (they only live for the open drawer) and clear any lingering upload error,
  // since the pending files that produced it have just been discarded.
  useEffect(() => {
    if (isOpen) {
      setDraftUrls(value);
      setShowUnsavedPrompt(false);
      setFailedUrls(new Set());
      return;
    }
    setDraftUrls(null);
    pendingFilesRef.current.forEach((_file, blobUrl) => URL.revokeObjectURL(blobUrl));
    pendingFilesRef.current = new Map();
    setFailedUrls(new Set());
    uploads.resetErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Revoke any still-pending blob URLs on unmount.
  useEffect(
    () => () => {
      pendingFilesRef.current.forEach((_file, blobUrl) => URL.revokeObjectURL(blobUrl));
    },
    [],
  );

  const previewFor = useCallback(
    (url: string) => blob.getPreview(url) ?? previewForProp?.(url),
    [blob, previewForProp],
  );

  const handleOpen = useCallback(() => {
    uploads.resetErrors();
    onFilesRejected?.({ oversized: false, wrongType: false, total: 0 });
    setInternalOpen(true);
  }, [uploads, onFilesRejected]);

  const closeDrawer = useCallback(() => {
    if (isControlled) {
      onCloseProp?.();
    } else {
      setInternalOpen(false);
    }
  }, [isControlled, onCloseProp]);

  const handleConfirm = useCallback(async () => {
    // Clear any prior failed-tile highlights before the new attempt so the
    // retry visually starts from a clean slate; they get re-applied below
    // if this attempt also fails.
    setFailedUrls(new Set());
    const pending = pendingFilesRef.current;
    let next = drawerUrls;

    if (pending.size > 0) {
      // Collect pending blob URLs in draft order so the final list keeps the
      // user's chosen order (including any drag-reorders made in the drawer).
      const blobUrlsInOrder = drawerUrls.filter((u) => pending.has(u));
      const filesInOrder = blobUrlsInOrder.map((b) => pending.get(b) as File);
      onUploadAttempt?.({ source: 'drawer' });
      const results = await uploads.upload(filesInOrder);
      if (results.length !== filesInOrder.length) {
        // Hard failure (e.g. no slots): mark the whole batch failed, keep drawer open.
        setFailedUrls(new Set(blobUrlsInOrder));
        onUploadError?.({ source: 'drawer' });
        return;
      }

      // Keep good, retry failed: promote succeeded photos to object URLs; leave
      // failed ones as pending blobs flagged in `failedUrls`.
      const blobToObject = new Map<string, string>();
      const failed = new Set<string>();
      results.forEach((result, i) => {
        const blobUrl = blobUrlsInOrder[i];
        if (result.ok) {
          blobToObject.set(blobUrl, result.objectUrl);
          // Attach a fresh blob preview keyed by the new object URL so the
          // trigger thumbnail keeps rendering immediately after confirm.
          blob.attach(result.objectUrl, pending.get(blobUrl) as File);
          // Succeeded blobs stay in `pendingFilesRef` (revoked on close); they
          // drop out of the draft via promotion, so a retry never re-uploads them.
        } else {
          failed.add(blobUrl);
        }
      });

      const promoted = drawerUrls.map((u) => blobToObject.get(u) ?? u);

      if (failed.size > 0) {
        setDraftUrls(promoted);
        setFailedUrls(failed);
        onUploadError?.({ source: 'drawer' });
        return; // keep the drawer open so the customer can retry or remove
      }
      next = promoted;
    }

    // Detach blob previews for previously-committed URLs the user removed.
    value.forEach((u) => {
      if (!next.includes(u)) {
        blob.detach(u);
      }
    });

    onChange(next);
    if (onConfirmProp) {
      onConfirmProp();
    } else {
      closeDrawer();
    }
  }, [
    drawerUrls,
    value,
    uploads,
    blob,
    onChange,
    onConfirmProp,
    closeDrawer,
    onUploadAttempt,
    onUploadError,
  ]);

  // Overlay/Esc dismiss: auto-confirm if the only edits are new uploads, or
  // surface the unsaved-changes banner for removals/reorders / unresolved
  // upload errors so the customer doesn't silently lose state.
  const handleDismiss = useCallback(() => {
    const kept = drawerUrls.filter((u) => value.includes(u));
    const isDestructive = kept.length < value.length || kept.some((u, i) => u !== value[i]);
    const hasAdditions = pendingFilesRef.current.size > 0;
    const hasUploadError = uploads.uploadError || uploads.presignError;

    if (isDestructive || hasUploadError) {
      setShowUnsavedPrompt(true);
    } else if (hasAdditions) {
      handleConfirm();
    } else {
      closeDrawer();
    }
  }, [drawerUrls, value, handleConfirm, closeDrawer, uploads.uploadError, uploads.presignError]);

  const handleFiles = useCallback(
    (files: File[]) => {
      onPickAttempt?.();
      if (files.length === 0) {
        return;
      }
      // Stage the file locally. The PUT runs on confirm; until then the blob
      // URL serves as both the draft entry and the preview src. `files` is
      // already trimmed to the drawer's remaining capacity by the hook.
      const blobUrls = files.map((f) => {
        const blobUrl = URL.createObjectURL(f);
        pendingFilesRef.current.set(blobUrl, f);
        return blobUrl;
      });
      setDraftUrls([...drawerUrls, ...blobUrls]);
    },
    [drawerUrls, onPickAttempt],
  );

  // Inline upload path used when the user picks/drops files on the empty
  // trigger (drawer never opens). The PUT runs immediately and the result
  // is committed via `onChange`, so the trigger transitions straight to its
  // filled state. The drawer remains the only path for edit/reorder/add-more.
  const handleInlineFiles = useCallback(
    async (files: File[]) => {
      onPickAttempt?.();
      // `files` is already trimmed to the trigger's remaining capacity by
      // the hook; bail out fast on empty / mid-upload to avoid concurrent PUTs.
      if (files.length === 0 || uploads.isUploading) {
        return;
      }

      uploads.resetErrors();
      onUploadAttempt?.({ source: 'inline' });
      const results = await uploads.upload(files);
      if (results.length !== files.length) {
        onUploadError?.({ source: 'inline' });
        return;
      }

      // Commit the successful photos; failures surface inline via `uploadError`
      // and the customer re-adds the missing ones through the trigger.
      results.forEach((result, i) => {
        if (result.ok) {
          blob.attach(result.objectUrl, files[i]);
        }
      });
      const succeededUrls = results.filter((result) => result.ok).map((result) => result.objectUrl);
      if (succeededUrls.length > 0) {
        onChange([...value, ...succeededUrls]);
      }
      if (succeededUrls.length !== results.length) {
        onUploadError?.({ source: 'inline' });
      }
    },
    [value, uploads, blob, onChange, onPickAttempt, onUploadAttempt, onUploadError],
  );

  // When the last failed tile is cleared (either retried or removed) we also
  // dismiss the upload error message — the failed state no longer exists,
  // so the alert would otherwise linger with nothing to act on.
  const clearFailed = useCallback(
    (url: string) => {
      if (!failedUrls.has(url)) {
        return;
      }
      const next = new Set(failedUrls);
      next.delete(url);
      setFailedUrls(next);
      if (next.size === 0) {
        uploads.resetErrors();
      }
    },
    [failedUrls, uploads],
  );

  const handleRetry = useCallback(
    (url: string) => {
      // File stays staged in `pendingFilesRef`, so the next Confirm picks it
      // up and retries the PUT.
      clearFailed(url);
    },
    [clearFailed],
  );

  const handleDelete = useCallback(
    (url: string) => {
      if (pendingFilesRef.current.has(url)) {
        URL.revokeObjectURL(url);
        pendingFilesRef.current.delete(url);
      }
      setDraftUrls((current) => (current ?? value).filter((u) => u !== url));
      clearFailed(url);
    },
    [value, clearFailed],
  );

  const handleFilesRejected = useCallback(
    (rejected: { oversized: File[]; wrongType: File[] }) => {
      if (!onFilesRejected) {
        return;
      }
      // A single file may fail both checks; union by reference so the consumer
      // sees the count of unique invalid files, not the sum of reasons.
      const allRejected = new Set([...rejected.oversized, ...rejected.wrongType]);
      onFilesRejected({
        oversized: rejected.oversized.length > 0,
        wrongType: rejected.wrongType.length > 0,
        total: allRejected.size,
      });
    },
    [onFilesRejected],
  );

  const handleReorder = useCallback(
    (from: number, to: number) => {
      setDraftUrls((current) => {
        const next = (current ?? value).slice();
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
    },
    [value],
  );

  const drawerUploadErrorMessage = uploads.uploadError
    ? mergedCopy.drawerUploadErrorMessage
    : uploads.presignError
    ? mergedCopy.presignErrorMessage
    : undefined;

  return (
    <>
      {!isControlled && (
        <>
          <PhotoPickerTrigger
            acceptedFileTypes={acceptedFileTypes}
            addMoreLabel={mergedCopy.triggerAddMoreLabel}
            editTileLabel={mergedCopy.triggerEditTileLabel}
            emptyLabel={mergedCopy.triggerEmptyLabel}
            erroredUrls={erroredUrls}
            isUploading={!isOpen && uploads.isUploading}
            maxFileSizeBytes={maxFileSizeBytes}
            maxFiles={Math.max(0, max - value.length)}
            onClick={handleOpen}
            onFilesRejected={handleFilesRejected}
            onFilesSelected={handleInlineFiles}
            previewFor={previewFor}
            triggerLabel={triggerLabel}
            uploadingLabel={mergedCopy.uploadingLabel}
            urls={value}
          />
          {!isOpen &&
            rejectionLines &&
            (rejectionLines.primary.length > 0 || rejectionLines.subDetails.length > 0) && (
              <S.InlineFeedbackList>
                {rejectionLines.primary.map((msg) => (
                  <S.InlineFeedbackRow key={`inline-rejection-primary-${msg}`} role="alert">
                    <Feedback error iconAlign="left" name={msg} />
                  </S.InlineFeedbackRow>
                ))}
                {rejectionLines.subDetails.map((msg) => (
                  <S.InlineFeedbackRow $indented key={`inline-rejection-sub-${msg}`} role="alert">
                    <Feedback error name={msg} />
                  </S.InlineFeedbackRow>
                ))}
              </S.InlineFeedbackList>
            )}
          {!isOpen && (uploads.uploadError || uploads.presignError) && (
            <S.InlineFeedbackList>
              <S.InlineFeedbackRow role="alert">
                <Feedback
                  error
                  iconAlign="left"
                  name={
                    uploads.presignError
                      ? mergedCopy.presignErrorMessage
                      : mergedCopy.uploadErrorMessage
                  }
                />
              </S.InlineFeedbackRow>
            </S.InlineFeedbackList>
          )}
        </>
      )}
      <PhotoPickerDrawer
        cancelLabel={mergedCopy.cancelLabel}
        confirmDisabled={
          drawerUrls.length === 0 ||
          uploads.isUploading ||
          uploads.uploadError ||
          uploads.presignError ||
          (required && min > 0 && drawerUrls.length < min)
        }
        confirmLabel={mergedCopy.confirmLabel}
        describedById={describedById}
        heading={mergedCopy.drawerHeading}
        isOpen={isOpen}
        max={max}
        onCancel={closeDrawer}
        onConfirm={handleConfirm}
        onDismiss={handleDismiss}
        rejectionLines={rejectionLines}
        requirementsLines={requirementsLines}
        subDescription={mergedCopy.drawerSubDescription}
        unsavedChangesMessage={showUnsavedPrompt ? mergedCopy.unsavedChangesMessage : undefined}
        uploadErrorMessage={drawerUploadErrorMessage}
      >
        <S.DrawerPhotoGrid>
          {drawerUrls.length > 0 && (
            <PhotoPickerGrid
              deleteLabel={mergedCopy.deleteLabel}
              erroredUrls={erroredUrls}
              failedUrls={failedUrls}
              onDelete={handleDelete}
              onReorder={handleReorder}
              onRetry={handleRetry}
              previewFor={previewFor}
              retryLabel={mergedCopy.retryLabel}
              urls={drawerUrls}
            />
          )}
          {drawerUrls.length < max && (
            <PhotoPickerDropzone
              acceptedFileTypes={acceptedFileTypes}
              addMoreLabel={mergedCopy.dropzoneAddMoreLabel}
              emptyBody={mergedCopy.dropzoneEmptyBody}
              isEmpty={drawerUrls.length === 0}
              maxFileSizeBytes={maxFileSizeBytes}
              maxFiles={max - drawerUrls.length}
              onFilesRejected={handleFilesRejected}
              onFilesSelected={handleFiles}
            />
          )}
        </S.DrawerPhotoGrid>
        {uploads.isUploading && <S.StatusMessage>{mergedCopy.uploadingLabel}</S.StatusMessage>}
        {required && min > 0 && drawerUrls.length < min && (
          <S.MinWarning>
            {format(mergedCopy.minPhotosMessage, { min, pluralS: min === 1 ? '' : 's' })}
          </S.MinWarning>
        )}
      </PhotoPickerDrawer>
    </>
  );
}
